<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  icon: {
    type: Array,
    default: () => ['fas', 'circle-info']
  },
  title: {
    type: String,
    default: 'Export Warning'
  },
  storageKey: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['confirm', 'cancel'])

const dontShowAgain = ref(false)

function handleConfirm() {
  if (dontShowAgain.value) {
    localStorage.setItem(props.storageKey, 'true')
  }
  dontShowAgain.value = false
  emit('confirm')
}

function handleCancel() {
  dontShowAgain.value = false
  emit('cancel')
}

function handleKeydown(event) {
  if (event.key === 'Escape' && props.show) {
    handleCancel()
  }
}

// Prevent body scroll when modal is open
watch(() => props.show, (isShown) => {
  if (isShown) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})

</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="modal-overlay" @click.self="handleCancel">
        <div class="export-warning-modal">
          <!-- Header -->
          <div class="modal-header">
            <div class="header-icon">
              <font-awesome-icon :icon="icon" />
            </div>
            <h3 class="header-title">{{ title }}</h3>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <slot />
          </div>

          <!-- Don't show again -->
          <label class="dont-show-again">
            <input type="checkbox" v-model="dontShowAgain" />
            <span>Don't show this again</span>
          </label>

          <!-- Actions -->
          <div class="modal-actions">
            <button @click="handleCancel" class="btn btn-cancel">Cancel</button>
            <button @click="handleConfirm" class="btn btn-confirm">
              <font-awesome-icon :icon="['fas', 'download']" />
              <span>Continue Export</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.export-warning-modal {
  background: var(--surface-page);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 460px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.header-icon {
  width: 40px;
  height: 40px;
  background: rgba(245, 158, 11, 0.2);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-warning);
  font-size: 1rem;
}

.header-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

/* Body */
.modal-body {
  padding: 1.5rem;
  color: var(--text-primary);
  font-size: 0.9rem;
  line-height: 1.6;
}

.modal-body :deep(p) {
  margin: 0 0 0.75rem 0;
}

.modal-body :deep(p:last-child) {
  margin-bottom: 0;
}

.modal-body :deep(code) {
  background: var(--surface-overlay-3);
  padding: 0.15rem 0.4rem;
  border-radius: var(--radius-xs);
  font-size: 0.85em;
  color: var(--color-warning);
}

/* Don't show again */
.dont-show-again {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1.5rem 1rem;
  cursor: pointer;
  color: var(--text-faint);
  font-size: 0.8rem;
  user-select: none;
}

.dont-show-again input[type="checkbox"] {
  accent-color: var(--accent);
  width: 14px;
  height: 14px;
  cursor: pointer;
}

/* Actions */
.modal-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border-radius: var(--radius-md);
  border: 1px solid;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: var(--transition);
}

.btn-cancel {
  background: transparent;
  border-color: var(--border-subtle);
  color: var(--text-muted);
}

.btn-cancel:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--text-primary);
}

.btn-confirm {
  flex: 1;
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.3);
  color: var(--color-success);
}

.btn-confirm:hover {
  background: rgba(16, 185, 129, 0.25);
  border-color: var(--color-success);
}

/* Animations */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.25s ease;
}

.modal-enter-active .export-warning-modal,
.modal-leave-active .export-warning-modal {
  transition: all 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .export-warning-modal,
.modal-leave-to .export-warning-modal {
  transform: scale(0.95) translateY(-10px);
  opacity: 0;
}
</style>
